/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { setGlobalDevModeChecks } from 'reselect';
setGlobalDevModeChecks({ identityFunctionCheck: 'never' });

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import 'bootstrap/dist/css/bootstrap.css';
import './global.scss';
import './app.scss';
import './assets/gsi-material-button.css';

import App from 'containers/App';
import LanguageProvider from 'containers/LanguageProvider';

import axios from 'axios';
import configureStore from './configureStore';

import { translationMessages } from './i18n';

import initPaymentProcessor from './paymentProcessor';
import initGTM from './initGTM';

axios.interceptors.request.use(function (config) {
  const { token } = localStorage;
  if (token) {
    config.headers.Authorization = token;
  }

  return config;
});

initGTM();
initPaymentProcessor();

const initialState = {};
const store = configureStore(initialState);
const MOUNT_NODE = document.getElementById('app');
const root = createRoot(MOUNT_NODE);
const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

const googleClientId = process.env.VITE_GOOGLE_CLIENT_ID;

if (!googleClientId) {
  console.warn(
    '[app] VITE_GOOGLE_CLIENT_ID is not set. Google login/signup will be disabled.',
  );
}

const renderApp = (messages) => {
  const inner = (
    <LanguageProvider messages={messages}>
      <BrowserRouter future={routerFutureFlags}>
        <App />
      </BrowserRouter>
    </LanguageProvider>
  );

  root.render(
    <Provider store={store}>
      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          {inner}
        </GoogleOAuthProvider>
      ) : (
        inner
      )}
    </Provider>,
  );
};

if (import.meta.hot) {
  import.meta.hot.accept(['./i18n', 'containers/App'], () => {
    renderApp(translationMessages);
  });
}

if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en.js')]))
    .then(() => renderApp(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  renderApp(translationMessages);
}

// Service worker registration is handled by the build tooling (offline-plugin for webpack, vite-plugin-pwa for Vite)
